import { useParams } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Building2,
  User,
  MoreHorizontal,
  Pencil,
  Users,
  Trash2,
  Plus,
  Shield,
  Crown,
} from 'lucide-react';
import { RouteParams } from '@/router/type';
import { useDemoOrganizationStore } from '@/store/demo-organization';
import { useDemoWorkspaceStore, DemoWorkspace } from '@/store/demo-workspace';

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'Owner':
      return <Crown className="h-3 w-3 text-yellow-500" />;
    case 'Admin':
      return <Shield className="h-3 w-3 text-blue-500" />;
    default:
      return <User className="h-3 w-3 text-muted-foreground" />;
  }
};

const DemoOrgWorkspacesSettings = () => {
  const { orgId } = useParams<RouteParams>();
  const organizations = useDemoOrganizationStore((state) => state.organizations);
  const workspaces = useDemoWorkspaceStore((state) => state.workspaces);
  const updateWorkspace = useDemoWorkspaceStore((state) => state.updateWorkspace);
  const deleteWorkspace = useDemoWorkspaceStore((state) => state.deleteWorkspace);
  const addWorkspace = useDemoWorkspaceStore((state) => state.addWorkspace);

  const organization = organizations.find((org) => org.id === orgId);
  const orgWorkspaces = workspaces.filter((ws) => ws.organizationId === orgId);

  // Dialog states
  const [editingWorkspace, setEditingWorkspace] = useState<DemoWorkspace | null>(null);
  const [deletingWorkspace, setDeletingWorkspace] = useState<DemoWorkspace | null>(null);
  const [viewingMembers, setViewingMembers] = useState<DemoWorkspace | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  // Form states
  const [editName, setEditName] = useState('');
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  if (!organization) {
    return (
      <div className="container py-8">
        <p className="text-muted-foreground">Organization not found</p>
      </div>
    );
  }

  const handleEditWorkspace = (workspace: DemoWorkspace) => {
    setEditingWorkspace(workspace);
    setEditName(workspace.name);
  };

  const handleSaveEdit = () => {
    if (editingWorkspace && editName.trim()) {
      updateWorkspace(editingWorkspace.id, { name: editName.trim() });
      setEditingWorkspace(null);
      setEditName('');
    }
  };

  const handleDeleteWorkspace = () => {
    if (deletingWorkspace && deleteConfirmText === deletingWorkspace.name) {
      deleteWorkspace(deletingWorkspace.id);
      setDeletingWorkspace(null);
      setDeleteConfirmText('');
    }
  };

  const handleCreateWorkspace = () => {
    if (newWorkspaceName.trim()) {
      const newWorkspace: DemoWorkspace = {
        id: `ws-${Date.now()}`,
        name: newWorkspaceName.trim(),
        organizationId: orgId!,
        type: 'workspace',
        role: 'Admin',
        quota: {
          h200: { total: 0, used: 0 },
          a100: { total: 0, used: 0 },
          l4: { total: 0, used: 0 },
        },
        instances: [],
        members: [
          {
            id: 'member-owner',
            email: 'admin@float16.cloud',
            name: 'Admin User',
            role: 'Owner',
            permissions: { read: true, create: true, delete: true },
            joinedAt: new Date().toISOString(),
          },
        ],
        createdAt: new Date().toISOString(),
      };
      addWorkspace(newWorkspace);
      setIsCreating(false);
      setNewWorkspaceName('');
    }
  };

  return (
    <div className="container py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Workspaces</h1>
          <p className="text-muted-foreground">
            Manage workspaces in {organization.name}
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Workspace
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-500" />
            Workspaces ({orgWorkspaces.length})
          </CardTitle>
          <CardDescription>
            All workspaces under this organization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {orgWorkspaces.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No workspaces in this organization. Create one to get started.
            </p>
          ) : (
            orgWorkspaces.map((ws) => (
              <div
                key={ws.id}
                className="flex items-center justify-between p-4 rounded-lg border"
              >
                <div className="flex items-center gap-4">
                  {ws.type === 'personal' ? (
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                  ) : (
                    <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-500" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{ws.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {ws.type === 'personal' ? 'Personal' : 'Team'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {ws.members.length} member{ws.members.length !== 1 ? 's' : ''} •{' '}
                      {Object.values(ws.quota).reduce((sum, q) => sum + q.total, 0)} GPU quota
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewingMembers(ws)}
                  >
                    <Users className="h-4 w-4 mr-1" />
                    Members
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditWorkspace(ws)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setViewingMembers(ws)}>
                        <Users className="h-4 w-4 mr-2" />
                        Manage Members
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setDeletingWorkspace(ws)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Workspace
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Edit Workspace Dialog */}
      <Dialog open={!!editingWorkspace} onOpenChange={() => setEditingWorkspace(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Workspace</DialogTitle>
            <DialogDescription>
              Enter a new name for this workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Workspace Name</Label>
              <Input
                id="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter workspace name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingWorkspace(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={!editName.trim()}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Workspace Dialog */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Workspace</DialogTitle>
            <DialogDescription>
              Create a new workspace in {organization.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-name">Workspace Name</Label>
              <Input
                id="new-name"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                placeholder="Enter workspace name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreating(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWorkspace} disabled={!newWorkspaceName.trim()}>
              Create Workspace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Workspace Dialog */}
      <AlertDialog open={!!deletingWorkspace} onOpenChange={() => setDeletingWorkspace(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workspace</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the workspace
              <span className="font-semibold"> {deletingWorkspace?.name}</span> and all its data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="confirm">
              Type <span className="font-semibold">{deletingWorkspace?.name}</span> to confirm
            </Label>
            <Input
              id="confirm"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Enter workspace name"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWorkspace}
              disabled={deleteConfirmText !== deletingWorkspace?.name}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Workspace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Members Dialog */}
      <Dialog open={!!viewingMembers} onOpenChange={() => setViewingMembers(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Workspace Members - {viewingMembers?.name}</DialogTitle>
            <DialogDescription>
              Manage members and their permissions for this workspace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
            {viewingMembers?.members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 rounded-lg border"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs">
                      {member.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{member.name}</span>
                      {getRoleIcon(member.role)}
                    </div>
                    <p className="text-xs text-muted-foreground">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs capitalize">
                    {member.role}
                  </Badge>
                  <div className="flex gap-1">
                    {member.permissions.read && (
                      <Badge variant="secondary" className="text-xs">Read</Badge>
                    )}
                    {member.permissions.create && (
                      <Badge variant="secondary" className="text-xs">Create</Badge>
                    )}
                    {member.permissions.delete && (
                      <Badge variant="secondary" className="text-xs">Delete</Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingMembers(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoOrgWorkspacesSettings;
